import { Divider, Modal, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { FC, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useResponsiveText } from '../../hooks';
import { useMaxScreen } from '../../hooks/use-max-screen';
import { innerText } from '../../util';

interface Props {
  'data-testid'?: string;
  calendar: JSX.Element;
  cancelButton: JSX.Element;
  disableEscapeKeyDown: boolean;
  endDatePicker: JSX.Element;
  endTimePicker: JSX.Element;
  isDateTimePickerOpen: boolean;
  onCancel: () => void;
  resetButton: JSX.Element;
  setRangeButton: JSX.Element;
  startDatePicker: JSX.Element;
  startTimePicker: JSX.Element;
}

interface StyledDividerProps {
  mediumScreen: boolean;
}

const StyledModal = styled(Modal)({
  zIndex: 1350,
});

const StyledDivider = styled(Divider)<StyledDividerProps>(({ mediumScreen }) => ({
  alignSelf: 'center',
  width: mediumScreen ? '90%' : '100%',
}));

export const DateTimeRangeLayoutDesktop: FC<Props> = ({
  calendar,
  cancelButton,
  disableEscapeKeyDown,
  endDatePicker,
  endTimePicker,
  isDateTimePickerOpen,
  onCancel,
  resetButton,
  setRangeButton,
  startDatePicker,
  startTimePicker,
  ...props
}): JSX.Element => {
  const { t } = useTranslation(['common']);
  const mediumScreen = useMaxScreen('md');

  const titleText = t('common:component.date-picker.labels.custom-date-range');
  const h6Styles = useResponsiveText('1.25rem', innerText(titleText).length, {
    minFontSize: '1rem',
    maxFontSize: '1.25rem',
    textLengthThreshold: 20,
  });

  return (
    <StyledModal
      disableEscapeKeyDown={disableEscapeKeyDown}
      open={isDateTimePickerOpen}
      onClose={onCancel}
    >
      <Stack
        spacing={2}
        borderRadius={1} left="50%" p={3} position="absolute" top="50%" width="fit-content" style={{ background: 'white', outline: 'none', transform: 'translate(-50%, -50%)' }}
        data-testid={props['data-testid']}
      >
        <Typography
          variant="h6"
          style={{
            fontSize: h6Styles.fontSize,
            lineHeight: h6Styles.lineHeight,
            wordBreak: h6Styles.wordBreak,
            whiteSpace: h6Styles.whiteSpace,
            overflow: h6Styles.overflow,
            textOverflow: h6Styles.textOverflow,
          }}
        >
          {titleText}
        </Typography>
        <Stack direction="row" spacing={mediumScreen ? 1 : 8}>
          <Stack>
            {calendar}
            <StyledDivider mediumScreen={mediumScreen} />
            {resetButton}
          </Stack>
          <Stack justifyContent="space-between" width={mediumScreen ? '210px' : '250px'}>
            <Stack mt={2}>
              <Typography pb={2}>{t('common:component.date-picker.labels.start-time').toLocaleUpperCase()}</Typography>
              {startDatePicker}
              {startTimePicker}
            </Stack>
            <Stack>
              <Typography pb={2}>{t('common:component.date-picker.labels.end-time').toLocaleUpperCase()}</Typography>
              {endDatePicker}
              {endTimePicker}
            </Stack>
            <Stack direction="row" justifyContent="space-evenly">
              {cancelButton}
              {setRangeButton}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </StyledModal>
  );
};
